/* eslint-disable*/
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faEye,
  faSyncAlt,
  faPaperPlane,
  faBell,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { hasPermission } from "../../helpers/permission.js";

const SLOTS = [
  { value: "preopen", label: "Pre-Open" },
  { value: "midday", label: "Mid-Session" },
  { value: "close", label: "Closing Bell" },
  { value: "mood", label: "Mood Shift" },
];
const CHANNELS = [
  { value: "telegram", label: "Telegram" },
  { value: "push", label: "Push" },
];
const STATUSES = ["generated", "pending", "sent", "failed"];

const STATUS_CLASS = {
  sent: "bg-success",
  failed: "bg-danger",
  pending: "bg-warning text-dark",
  generated: "bg-info text-dark",
};

// Today's date in IST as YYYY-MM-DD
const todayIST = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const formatTradingDate = (value) => {
  if (!value) return "-";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return d && m && y ? `${d}-${m}-${y}` : value;
};

const slotLabel = (slot) => SLOTS.find((s) => s.value === slot)?.label || slot;
const channelLabel = (ch) =>
  CHANNELS.find((c) => c.value === ch)?.label || ch;

const StatusBadge = ({ status }) =>
  status ? (
    <span className={`badge ${STATUS_CLASS[status] || "bg-secondary"}`}>
      {status}
    </span>
  ) : null;

const SourceBadge = ({ source }) =>
  source ? (
    <span
      className={`badge ${source === "manual" ? "bg-primary" : "bg-secondary"} ms-1`}
    >
      {source}
    </span>
  ) : null;

const preLine = { whiteSpace: "pre-line", wordBreak: "break-word" };

// "Send to Telegram" / "Send to Push" for a row the API says can be sent
const SendButton = ({ row, sending, onSend, className = "" }) =>
  row?.can_send && hasPermission("streetview", "add") ? (
    <button
      className={`btn btn-success btn-xs ${className}`}
      disabled={sending}
      onClick={() => onSend(row)}
    >
      <FontAwesomeIcon
        icon={sending ? faSyncAlt : row.channel === "telegram" ? faPaperPlane : faBell}
        spin={sending}
        className="me-1"
      />
      {sending ? "Sending..." : `Send to ${channelLabel(row.channel)}`}
    </button>
  ) : null;

// One channel's alert inside a slot card on the day board
const ChannelBlock = ({ channel, alert, onView, sending, onSend }) => (
  <div className="border rounded p-2 mb-2 bg-light">
    <div className="d-flex justify-content-between align-items-center mb-1">
      <strong style={{ fontSize: "0.85rem" }}>
        <FontAwesomeIcon
          icon={channel === "telegram" ? faPaperPlane : faBell}
          className="me-1"
        />
        {channelLabel(channel)}
      </strong>
      {alert && (
        <span>
          <StatusBadge status={alert.status} />
          <SourceBadge source={alert.source} />
          <button
            className="btn btn-default btn-xs ms-2"
            title="View details"
            onClick={() => onView(alert.id)}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        </span>
      )}
    </div>
    {!alert ? (
      <div className="text-muted" style={{ fontSize: "0.8rem" }}>
        Not generated yet
      </div>
    ) : (
      <div style={{ fontSize: "0.8rem" }}>
        {channel === "push" && alert.subject && (
          <div className="fw-bold mb-1">{alert.subject}</div>
        )}
        <div
          style={{ ...preLine, maxHeight: 180, overflowY: "auto" }}
          className="mb-1"
        >
          {alert.body}
        </div>
        {alert.error_message && (
          <div className="text-danger mb-1">Error: {alert.error_message}</div>
        )}
        <div className="text-muted">
          Created: {formatDateTime(alert.created_at)}
          {alert.sent_at && <> &nbsp;|&nbsp; Sent: {formatDateTime(alert.sent_at)}</>}
        </div>
        <SendButton row={alert} sending={sending} onSend={onSend} className="mt-2" />
      </div>
    )}
  </div>
);

const StreetViewAlerts = () => {
  // Day board
  const [boardDate, setBoardDate] = useState(todayIST());
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Logs table
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [dateFilter, setDateFilter] = useState("");
  const [slotFilter, setSlotFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  // Preview modal
  const [preview, setPreview] = useState(null);
  const [previewSlot, setPreviewSlot] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Generate / send
  const canAdd = hasPermission("streetview", "add");
  const [generatingSlot, setGeneratingSlot] = useState("");
  const [sendingIds, setSendingIds] = useState([]);

  const fetchSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await axios.get(
        `${API_URL}newStreetView/alert-logs/summary`,
        {
          headers: authHeaders(),
          params: boardDate ? { date: boardDate } : {},
        }
      );
      setSummary(response.data?.data || null);
    } catch (err) {
      console.error("Error fetching alert summary:", err);
      showToast.error(err.response?.data?.message || "Failed to load alert summary");
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const params = { page, limit };
      if (dateFilter) params.date = dateFilter;
      if (slotFilter) params.slot = slotFilter;
      if (channelFilter) params.channel = channelFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get(`${API_URL}newStreetView/alert-logs`, {
        headers: authHeaders(),
        params,
      });
      const data = response.data?.data || {};
      setLogs(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching alert logs:", err);
      showToast.error(err.response?.data?.message || "Failed to load alert logs");
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [boardDate]);

  useEffect(() => {
    fetchLogs();
  }, [page, dateFilter, slotFilter, channelFilter, statusFilter]);

  // Sidebar menu click clears filters
  useEffect(() => {
    const handleClearEvent = () => {
      setBoardDate(todayIST());
      handleClearFilters();
    };
    window.addEventListener("clearStreetViewFiltersEvent", handleClearEvent);
    return () => {
      window.removeEventListener(
        "clearStreetViewFiltersEvent",
        handleClearEvent
      );
    };
  }, []);

  const handleClearFilters = () => {
    setDateFilter("");
    setSlotFilter("");
    setChannelFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const withPageReset = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const openDetail = async (id) => {
    setShowDetail(true);
    setDetail(null);
    try {
      setDetailLoading(true);
      const response = await axios.get(
        `${API_URL}newStreetView/alert-logs/${id}`,
        { headers: authHeaders() }
      );
      setDetail(response.data?.data || null);
    } catch (err) {
      showToast.error(err.response?.status === 404
          ? "Alert log not found"
          : err.response?.data?.message || "Failed to load alert log");
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openPreview = async (slot) => {
    setPreviewSlot(slot);
    setShowPreview(true);
    setPreview(null);
    try {
      setPreviewLoading(true);
      const response = await axios.get(`${API_URL}newStreetView/alert-preview`, {
        headers: authHeaders(),
        params: { slot },
      });
      setPreview(response.data?.data || null);
    } catch (err) {
      showToast.error(err.response?.data?.message || "Failed to build preview");
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // After generate/send: board jumps to today (generate is always today IST)
  const refreshAfterChange = () => {
    if (boardDate === todayIST()) fetchSummary();
    else setBoardDate(todayIST());
    fetchLogs();
  };

  const generateAlert = async (slot, force = false) => {
    try {
      setGeneratingSlot(slot);
      const response = await axios.post(
        `${API_URL}newStreetView/alert-generate`,
        { slot, force },
        { headers: authHeaders() }
      );
      const data = response.data?.data || {};
      showToast.success(
        response.data?.message || `${slotLabel(slot)} alert generated`
      );
      if (data.fallbackUsed) {
        showToast.warning(
          "Live data was missing, a generic no-numbers message was built"
        );
      }
      setShowPreview(false);
      refreshAfterChange();
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message;
      setGeneratingSlot("");

      // 409 (already generated) / 422 (not a trading day) can be overridden with force
      if (!force && (status === 409 || status === 422)) {
        const result = await Swal.fire({
          title: status === 409 ? "Already generated" : "Not a trading day",
          text:
            status === 409
              ? `${slotLabel(slot)} rows already exist for today. Regenerate? Rows already sent are kept as is.`
              : `Today is not a trading day. Generate ${slotLabel(slot)} anyway?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: status === 409 ? "Yes, regenerate" : "Yes, generate",
          cancelButtonText: "Cancel",
        });
        if (result.isConfirmed) return generateAlert(slot, true);
        return;
      }

      showToast.error(
        status === 403
          ? "You don't have permission to generate StreetView alerts"
          : message || "Failed to generate alert"
      );
    } finally {
      setGeneratingSlot((current) => (current === slot ? "" : current));
    }
  };

  const sendRow = async (row) => {
    const result = await Swal.fire({
      title: `Send to ${channelLabel(row.channel)}?`,
      text: `${slotLabel(row.slot)} alert for ${formatTradingDate(row.trading_date)} will be posted now.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, send",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setSendingIds((ids) => [...ids, row.id]);
    try {
      const response = await axios.post(
        `${API_URL}newStreetView/alert-logs/${row.id}/send`,
        {},
        { headers: authHeaders() }
      );
      const updated = response.data?.data;
      if (updated?.status === "failed") {
        showToast.error(
          `${channelLabel(row.channel)} send failed: ${updated.error_message || "Unknown error"}`
        );
      } else {
        showToast.success(
          response.data?.message || `Sent to ${channelLabel(row.channel)}`
        );
      }
      if (updated) {
        setLogs((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setDetail((d) => (d && d.id === updated.id ? { ...d, ...updated } : d));
      }
      fetchSummary();
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message;
      showToast.error(
        status === 403
          ? "You don't have permission to send StreetView alerts"
          : status === 404
          ? "Alert log not found"
          : status === 409
          ? message || "This alert is already sent or a send is in progress"
          : status === 422
          ? message || "This alert can't be sent"
          : message || "Failed to send alert"
      );
      refreshAfterChange();
    } finally {
      setSendingIds((ids) => ids.filter((id) => id !== row.id));
    }
  };

  const isSending = (row) => !!row && sendingIds.includes(row.id);
  const isToday = boardDate === todayIST();

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* Day board */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <div className="panel-heading border-0">
            <h4 className="panel-title">
              StreetView Alerts
              {summary && (
                <span className="ms-2" style={{ fontSize: "0.8rem" }}>
                  {formatTradingDate(summary.tradingDate)}{" "}
                  {summary.isTradingDay ? (
                    <span className="badge bg-success">Trading day</span>
                  ) : (
                    <span className="badge bg-secondary">Market holiday</span>
                  )}
                </span>
              )}
            </h4>
          </div>
          <div className="me-3 d-flex align-items-center">
            <input
              type="date"
              className="form-control form-control-sm me-2"
              value={boardDate}
              onChange={(e) => setBoardDate(e.target.value || todayIST())}
              style={{ width: 150 }}
            />
            <button
              className="btn btn-default btn-sm me-2"
              onClick={() => setBoardDate(todayIST())}
            >
              Today
            </button>
            <button
              className="btn btn-default btn-sm"
              title="Refresh"
              onClick={fetchSummary}
              disabled={summaryLoading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={summaryLoading} />
            </button>
          </div>
        </div>
        <div className="panel-body">
          <div className="row">
            {summaryLoading && !summary ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div className="col-md-6 col-xl-3 mb-3" key={`sk-${i}`}>
                  <div className="skeleton-box" style={{ height: 220 }}></div>
                </div>
              ))
            ) : summary?.slots?.length ? (
              summary.slots.map((slot) => (
                <div className="col-md-6 col-xl-3 mb-3" key={slot.slot}>
                  <div className="card h-100">
                    <div className="card-header d-flex justify-content-between align-items-center py-2">
                      <div>
                        <strong>{slot.label}</strong>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {slot.scheduledAt
                            ? `Scheduled ${slot.scheduledAt} IST`
                            : "Not scheduled"}
                          {slot.generateOnly && (
                            <span className="badge bg-info text-dark ms-1">
                              Generate only
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="d-flex">
                        <button
                          className="btn btn-default btn-xs"
                          title="Preview from live data (does not send)"
                          onClick={() => openPreview(slot.slot)}
                        >
                          Preview
                        </button>
                        {canAdd && (
                          <button
                            className="btn btn-primary btn-xs ms-1"
                            title={
                              isToday
                                ? "Build and store today's alert (does not send)"
                                : "Generate is only available for today"
                            }
                            disabled={!isToday || !!generatingSlot}
                            onClick={() => generateAlert(slot.slot)}
                          >
                            <FontAwesomeIcon
                              icon={generatingSlot === slot.slot ? faSyncAlt : faBolt}
                              spin={generatingSlot === slot.slot}
                              className="me-1"
                            />
                            {slot.telegram || slot.push ? "Regenerate" : "Generate"}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="card-body p-2">
                      <ChannelBlock
                        channel="telegram"
                        alert={slot.telegram}
                        onView={openDetail}
                        sending={isSending(slot.telegram)}
                        onSend={sendRow}
                      />
                      <ChannelBlock
                        channel="push"
                        alert={slot.push}
                        onView={openDetail}
                        sending={isSending(slot.push)}
                        onSend={sendRow}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-4 text-muted">
                No alert data for this date
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logs table */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Alert Logs</h4>
          </div>
          <div className="me-3">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-condensed table-hover bg-white">
            <thead className="thead-light">
              <tr>
                <th width="4%" className="text-center">#</th>
                <th width="10%">Trading Date</th>
                <th width="10%">Slot</th>
                <th width="9%">Channel</th>
                <th width="29%">Message</th>
                <th width="8%">Status</th>
                <th width="9%">Created</th>
                <th width="9%">Sent</th>
                <th width="12%">Actions</th>
              </tr>
              <tr>
                <th></th>
                <th>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateFilter}
                    onChange={withPageReset(setDateFilter)}
                  />
                </th>
                <th>
                  <select
                    className="form-control-sm"
                    value={slotFilter}
                    onChange={withPageReset(setSlotFilter)}
                  >
                    <option value="">All Slots</option>
                    {SLOTS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    className="form-control-sm"
                    value={channelFilter}
                    onChange={withPageReset(setChannelFilter)}
                  >
                    <option value="">All</option>
                    {CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </th>
                <th></th>
                <th>
                  <select
                    className="form-control-sm"
                    value={statusFilter}
                    onChange={withPageReset(setStatusFilter)}
                  >
                    <option value="">All</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </th>
                <th colSpan="3">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleClearFilters}
                    style={{ padding: "2px 6px", fontSize: "12px", lineHeight: "2" }}
                  >
                    Clear
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="skeleton-row">
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j}>
                        <div className="skeleton-box"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs.length > 0 ? (
                logs.map((row, index) => (
                  <tr key={row.id}>
                    <td className="text-center">{(page - 1) * limit + index + 1}</td>
                    <td>{formatTradingDate(row.trading_date)}</td>
                    <td>{slotLabel(row.slot)}</td>
                    <td>{channelLabel(row.channel)}</td>
                    <td style={{ fontSize: "0.8rem" }}>
                      {row.subject && <div className="fw-bold">{row.subject}</div>}
                      <div
                        style={{
                          ...preLine,
                          maxHeight: 60,
                          overflow: "hidden",
                        }}
                      >
                        {row.body}
                      </div>
                      {row.error_message && (
                        <div className="text-danger">{row.error_message}</div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={row.status} />
                      <SourceBadge source={row.source} />
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>{formatDateTime(row.created_at)}</td>
                    <td style={{ fontSize: "0.8rem" }}>{formatDateTime(row.sent_at)}</td>
                    <td className="text-nowrap">
                      <button
                        className="btn btn-default btn-xs me-1"
                        title="View details"
                        onClick={() => openDetail(row.id)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <SendButton row={row} sending={isSending(row)} onSend={sendRow} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No alert logs found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      <Modal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        size="lg"
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Alert Log {detail ? `#${detail.id}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading || !detail ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <>
              <table className="table table-sm table-bordered" style={{ fontSize: "0.85rem" }}>
                <tbody>
                  <tr>
                    <th width="25%">Trading Date</th>
                    <td>{formatTradingDate(detail.trading_date)}</td>
                  </tr>
                  <tr>
                    <th>Slot</th>
                    <td>{slotLabel(detail.slot)}</td>
                  </tr>
                  <tr>
                    <th>Channel</th>
                    <td>{channelLabel(detail.channel)}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <StatusBadge status={detail.status} />
                      <SourceBadge source={detail.source} />
                      {detail.generated_by && (
                        <span className="text-muted ms-2" style={{ fontSize: "0.8rem" }}>
                          by user #{detail.generated_by}
                        </span>
                      )}
                      <SendButton
                        row={detail}
                        sending={isSending(detail)}
                        onSend={sendRow}
                        className="ms-2"
                      />
                    </td>
                  </tr>
                  {detail.alert_id && (
                    <tr>
                      <th>Alert ID</th>
                      <td>{detail.alert_id}</td>
                    </tr>
                  )}
                  {detail.message_id && (
                    <tr>
                      <th>Message ID</th>
                      <td>{detail.message_id}</td>
                    </tr>
                  )}
                  {detail.subject && (
                    <tr>
                      <th>{detail.channel === "push" ? "Title" : "Subject"}</th>
                      <td>{detail.subject}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Body</th>
                    <td style={preLine}>{detail.body}</td>
                  </tr>
                  {detail.link_url && (
                    <tr>
                      <th>Link</th>
                      <td>
                        <a href={detail.link_url} target="_blank" rel="noreferrer">
                          {detail.link_url}
                        </a>
                      </td>
                    </tr>
                  )}
                  {detail.error_message && (
                    <tr>
                      <th>Error</th>
                      <td className="text-danger">{detail.error_message}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Created</th>
                    <td>{formatDateTime(detail.created_at)}</td>
                  </tr>
                  <tr>
                    <th>Sent</th>
                    <td>{formatDateTime(detail.sent_at)}</td>
                  </tr>
                </tbody>
              </table>
              <h6>Market Snapshot</h6>
              {detail.snapshot ? (
                <pre
                  className="bg-light border rounded p-2"
                  style={{ fontSize: "0.75rem", maxHeight: 300, overflow: "auto" }}
                >
                  {JSON.stringify(detail.snapshot, null, 2)}
                </pre>
              ) : (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  No snapshot stored for this alert
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Preview modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Preview: {slotLabel(previewSlot)}{" "}
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              (live data, not sent)
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading || !preview ? (
            <div className="text-center py-4">Building preview...</div>
          ) : (
            <>
              {preview.fallbackUsed && (
                <div className="alert alert-warning py-2" style={{ fontSize: "0.85rem" }}>
                  Fallback data was used to build this message.
                </div>
              )}
              <h6>
                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                Telegram
              </h6>
              <div
                className="bg-light border rounded p-2 mb-3"
                style={{ ...preLine, fontSize: "0.85rem" }}
              >
                {preview.telegramFullText || "-"}
              </div>
              <h6>
                <FontAwesomeIcon icon={faBell} className="me-1" />
                Push
              </h6>
              <div className="bg-light border rounded p-2" style={{ fontSize: "0.85rem" }}>
                <div className="fw-bold">{preview.pushTitle || "-"}</div>
                <div style={preLine}>{preview.pushBody}</div>
              </div>
            </>
          )}
        </Modal.Body>
        {canAdd && (
          <Modal.Footer>
            <span className="text-muted me-auto" style={{ fontSize: "0.8rem" }}>
              Generate stores today's alert for Telegram and Push. Nothing is sent until you click Send.
            </span>
            <button
              className="btn btn-default btn-sm"
              onClick={() => setShowPreview(false)}
            >
              Close
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={previewLoading || !!generatingSlot}
              onClick={() => generateAlert(previewSlot)}
            >
              <FontAwesomeIcon
                icon={generatingSlot === previewSlot ? faSyncAlt : faBolt}
                spin={generatingSlot === previewSlot}
                className="me-1"
              />
              Generate {slotLabel(previewSlot)}
            </button>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default StreetViewAlerts;
